export const PASSWORD_MIN_CHARACTERS = 8
export const PASSWORD_MAX_UTF8_BYTES = 72
export const PASSWORD_MIN_SCORE = 3

export type PasswordAssessmentStatus = 'neutral' | 'pending' | 'settled'

export interface PasswordAssessment {
  value: string
  generation: number
  status: PasswordAssessmentStatus
  characterCount: number
  byteCount: number
  score: number | null
  label: string | null
  accepted: boolean
}

export function passwordCharacterLength(value: string): number {
  return Array.from(value).length
}

export function passwordByteLength(value: string): number {
  return new TextEncoder().encode(value).length
}

export function passwordScoreLabel(score: number): string {
  if (score <= 1) return 'Weak'
  if (score === 2) return 'Fair'
  if (score === 3) return 'Strong'
  return 'Very strong'
}

export function passwordAssessment(
  value: string,
  generation = 0,
  status: PasswordAssessmentStatus = value ? 'pending' : 'neutral',
  score: number | null = null,
): PasswordAssessment {
  const characterCount = passwordCharacterLength(value)
  const byteCount = passwordByteLength(value)
  const settled = status === 'settled' && score !== null
  return {
    value,
    generation,
    status,
    characterCount,
    byteCount,
    score: settled ? score : null,
    label: settled ? passwordScoreLabel(score) : null,
    accepted: settled
      && characterCount >= PASSWORD_MIN_CHARACTERS
      && byteCount <= PASSWORD_MAX_UTF8_BYTES
      && score >= PASSWORD_MIN_SCORE,
  }
}

export async function scorePassword(value: string): Promise<number> {
  const module = await import('zxcvbn')
  return module.default(value).score
}

export function assessmentAccepts(
  assessment: PasswordAssessment,
  currentValue: string,
): boolean {
  return assessment.value === currentValue
    && assessment.status === 'settled'
    && assessment.accepted
}
